import { FC } from 'react';
import { useSetRecoilState } from 'recoil';
import { Redirect } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { ROUTE } from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { EMAIL_CONFIRMATION_MUTATION, EMAIL_VERIFIED_QUERY } from './queries';

import './EmailVerificationTrial.scss';

export type EmailVerificationData = {
  account: {
    isEmailVerified: boolean;
    isEmailVerificationRequired: boolean;
  };
};

/**
 * If a user is on a trial account and has not yet verified his or her email address, they will be sent to this page which
 * will allow them to re-send the verification email.
 */

const EmailVerificationTrialPage: FC = () => {
  const { data: accountData } = useQuery<EmailVerificationData>(EMAIL_VERIFIED_QUERY);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const showFailureAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.ERROR,
      autoHide: true,
      duration: 3,
    });
  };

  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 3,
    });
  };

  const [requestEmailConfirmation] = useMutation(EMAIL_CONFIRMATION_MUTATION, {
    onCompleted: (data: { requestEmailConfirmation: boolean }) => {
      if (data.requestEmailConfirmation) {
        showSuccessAlert('A confirmation e-mail has been sent.');
      } else {
        showFailureAlert("If you're redirected to this page again, please contact Support.");
      }
    },
    onError: (error: ApolloError) => {
      showFailureAlert(clearGraphQLErrorMessage(error.message));
    },
  });

  // If the email is already verified or not required redirect to the logStart page
  if (accountData?.account.isEmailVerified === true || !accountData?.account.isEmailVerificationRequired) {
    return <Redirect to={ROUTE.ROOT} />;
  }

  const handleResendEmail = (): void => {
    requestEmailConfirmation();
  };

  return (
    <Page docTitle="Email Verification Required">
      <PageHeader title="Account Verification" />
      <Card className="EmailVerification">
        <Card.Header className="EmailVerification__Header">Verification Required</Card.Header>
        <Card.Body>
          Please verify your email address to use this trial account. If you can&apos;t find the original verification
          email click the button below to send it again.
          <div className="EmailVerification__Button">
            <Button variant={ButtonVariant.PRIMARY} onClick={handleResendEmail}>
              Resend Verification Email
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default EmailVerificationTrialPage;
